import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const Badge = ({ children, tone = 'slate' }) => (
  <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-${tone}-100 text-${tone}-700`}>{children}</span>
);
const EVENT_TONE = { joiner: 'green', mover: 'blue', leaver: 'rose', rejoiner: 'amber' };
const STAFF_ROLES = ['school_admin', 'principal', 'registrar', 'admissions_officer', 'teacher', 'finance_admin'];

export default function Lifecycle() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canErase = ['global_admin', 'school_admin'].includes(user?.role);
  const { data: summary } = useQuery({ queryKey: ['lc-summary'], queryFn: () => api.get('/lifecycle/summary').then((r) => r.data) });
  const { data: events } = useQuery({ queryKey: ['lc-events'], queryFn: () => api.get('/lifecycle/events').then((r) => r.data) });
  const { data: checklists } = useQuery({ queryKey: ['lc-checklists'], queryFn: () => api.get('/lifecycle/checklists').then((r) => r.data) });
  const { data: users } = useQuery({ queryKey: ['lc-users'], queryFn: () => api.get('/users').then((r) => r.data) });
  const { data: students } = useQuery({ queryKey: ['lc-students'], queryFn: () => api.get('/students').then((r) => r.data) });
  const [form, setForm] = useState({ subject_type: 'staff', subject_id: '', event: 'joiner', detail: '', new_role: 'teacher', new_grade: '', new_section: '' });
  const invalidate = () => ['lc-summary', 'lc-events', 'lc-checklists', 'lc-users', 'lc-students'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const transMut = useMutation({
    mutationFn: () => api.post('/lifecycle/transition', { ...form, new_role: form.event === 'mover' ? form.new_role : undefined }),
    onSuccess: (r) => { toast.success(`Lifecycle event recorded${r.data.checklist_id ? ' - exit checklist opened' : ''}`); setForm({ ...form, subject_id: '', detail: '' }); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, key }) => api.post(`/lifecycle/checklists/${id}/toggle/${key}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lc-checklists'] }),
    onError: (e) => toast.error(errMsg(e)),
  });
  const { data: erasure } = useQuery({ queryKey: ['lc-erasure'], queryFn: () => api.get('/lifecycle/erasure/eligible').then((r) => r.data), enabled: canErase });
  const { data: certs } = useQuery({ queryKey: ['lc-certs'], queryFn: () => api.get('/lifecycle/erasure/certificates').then((r) => r.data), enabled: canErase });
  const { data: retention } = useQuery({ queryKey: ['lc-retention'], queryFn: () => api.get('/lifecycle/retention-settings').then((r) => r.data), enabled: canErase });
  const [retForm, setRetForm] = useState({ staff_days: '', student_days: '' });
  const retMut = useMutation({
    mutationFn: () => api.post('/lifecycle/retention-settings', {
      tenant_id: user?.tenant_id,
      staff_days: parseInt(retForm.staff_days, 10) || retention?.[0]?.staff_days || 365,
      student_days: parseInt(retForm.student_days, 10) || retention?.[0]?.student_days || 730,
      auto_erase: retAuto, grace_days: parseInt(retGrace, 10) || 30,
    }),
    onSuccess: () => { toast.success('Retention schedule saved'); ['lc-retention', 'lc-erasure'].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const [retAuto, setRetAuto] = useState(false);
  const [retGrace, setRetGrace] = useState('30');
  useEffect(() => {
    if (retention?.[0] && !retention[0].default) { setRetAuto(!!retention[0].auto_erase); setRetGrace(String(retention[0].grace_days ?? 30)); }
  }, [retention]);
  const eraseMut = useMutation({
    mutationFn: ({ subject_type, subject_id, reason }) => api.post('/lifecycle/erasure/execute', { subject_type, subject_id, reason }),
    onSuccess: (r) => { toast.success(`Erased & certified - ${r.data.certificate_hash.slice(0, 16)}...`); ['lc-erasure', 'lc-certs', 'lc-users', 'lc-students'].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const doErase = (subject_type, subject_id, name) => {
    if (!window.confirm(`Anonymise & purge ${name}? Personal identifiers are irreversibly erased. A deletion certificate will be issued.`)) return;
    const reason = window.prompt('Reason for erasure (recorded on the certificate):', 'Data subject erasure request');
    if (!reason) return;
    eraseMut.mutate({ subject_type, subject_id, reason });
  };

  const staffOptions = (users || []).filter((u) => STAFF_ROLES.includes(u.role));
  const subjects = form.subject_type === 'staff'
    ? staffOptions.map((u) => ({ id: u.id, label: `${u.name || u.email} (${u.role.replace(/_/g, ' ')}) - ${u.status}` }))
    : (students || []).map((s) => ({ id: s.id, label: `${s.name} - Grade ${s.grade}${s.section} - ${s.status}` }));

  return (
    <div data-testid="lifecycle-page">
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Joiner · Mover · Leaver</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">Lifecycle</h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="lifecycle-summary">
        {['joiner', 'mover', 'leaver', 'rejoiner'].map((e) => (
          <div key={e} className="bg-white border border-slate-200 rounded-md p-4">
            <p className="font-mono-label text-[10px] uppercase text-slate-400">{e}s · {summary?.window_days || 90}d</p>
            <p className="font-display text-2xl font-extrabold" data-testid={`lc-count-${e}`}>{summary?.counts?.[e] ?? '-'}</p>
          </div>
        ))}
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <p className="font-mono-label text-[10px] uppercase text-slate-400">Open checklists</p>
          <p className="font-display text-2xl font-extrabold" data-testid="lc-open-checklists">{summary?.open_checklists ?? '-'}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="lc-transition-form">
            <h2 className="font-display text-lg font-semibold mb-1">Record a transition</h2>
            <p className="text-sm text-slate-500 mb-4">Joiners are activated, movers change role or grade, leavers are suspended with an exit checklist. Every event is audited.</p>
            <div className="space-y-3">
              <div className="flex gap-2">
                <select data-testid="lc-subject-type" value={form.subject_type} onChange={(e) => setForm({ ...form, subject_type: e.target.value, subject_id: '' })}
                  className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                  <option value="staff">Staff / Faculty</option>
                  <option value="student">Student</option>
                </select>
                <select data-testid="lc-event-select" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })}
                  className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                  {['joiner', 'mover', 'leaver', 'rejoiner'].map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <select data-testid="lc-subject-select" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                <option value="">Select a person...</option>
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              {form.event === 'mover' && form.subject_type === 'staff' && (
                <select data-testid="lc-new-role" value={form.new_role} onChange={(e) => setForm({ ...form, new_role: e.target.value })}
                  className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                  {STAFF_ROLES.map((r) => <option key={r} value={r}>New role: {r.replace(/_/g, ' ')}</option>)}
                </select>
              )}
              {form.event === 'mover' && form.subject_type === 'student' && (
                <div className="flex gap-2">
                  <Input data-testid="lc-new-grade" placeholder="New grade (e.g. 8)" value={form.new_grade} onChange={(e) => setForm({ ...form, new_grade: e.target.value })} />
                  <Input data-testid="lc-new-section" placeholder="New section (e.g. B)" value={form.new_section} onChange={(e) => setForm({ ...form, new_section: e.target.value })} />
                </div>
              )}
              <Input data-testid="lc-detail-input" placeholder="Detail / reason (optional)" value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} />
              <Button data-testid="lc-transition-button" disabled={!form.subject_id || transMut.isPending}
                onClick={() => { if (form.event !== 'leaver' || window.confirm('Offboard this person? Their account will be suspended and an exit checklist opened.')) transMut.mutate(); }}
                className="bg-slate-900 hover:bg-slate-800">Record {form.event}</Button>
            </div>
          </div>

          <div data-testid="lc-checklists">
            <h2 className="font-display text-lg font-semibold mb-3">Exit checklists</h2>
            <div className="space-y-3">
              {(checklists || []).map((c) => (
                <div key={c.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`lc-checklist-${c.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <p className="font-semibold text-sm">{c.subject_name}</p>
                    <Badge tone="slate">{c.subject_type}</Badge>
                    <Badge tone={c.status === 'completed' ? 'green' : 'amber'}>{c.status}</Badge>
                    <span className="text-xs text-slate-400 ml-auto">{c.created_at?.slice(0, 10)}</span>
                  </div>
                  <div className="space-y-1">
                    {c.items.map((it) => (
                      <button key={it.key} data-testid={`lc-check-${c.id}-${it.key}`} onClick={() => toggleMut.mutate({ id: c.id, key: it.key })}
                        className={`w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded hover:bg-slate-50 ${it.done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                        <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${it.done ? 'bg-green-600 border-green-600 text-white' : 'border-slate-300'}`}>{it.done ? '✓' : ''}</span>
                        {it.label}
                        {it.done && it.done_by && <span className="text-[10px] text-slate-400 ml-auto no-underline">{it.done_by}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {(checklists || []).length === 0 && <p className="text-sm text-slate-400">No exit checklists yet.</p>}
            </div>
          </div>
        </div>

        <div data-testid="lc-timeline">
          <h2 className="font-display text-lg font-semibold mb-3">Event timeline</h2>
          <div className="space-y-2">
            {(events || []).map((ev) => (
              <div key={ev.id} className="bg-white border border-slate-200 rounded-md p-3 text-sm" data-testid={`lc-event-${ev.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge tone={EVENT_TONE[ev.event] || 'slate'}>{ev.event}</Badge>
                  <Badge>{ev.subject_type}</Badge>
                  <span className="font-medium">{ev.subject_name}</span>
                  <span className="text-xs text-slate-400 ml-auto">{ev.created_at?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                {ev.detail && <p className="text-xs text-slate-500 mt-1">{ev.detail}</p>}
                <p className="text-[10px] text-slate-400 mt-1">by {ev.actor}</p>
              </div>
            ))}
            {(events || []).length === 0 && <p className="text-sm text-slate-400">No lifecycle events yet. New hires, role changes, promotions and exits will appear here.</p>}
          </div>

          {canErase && (
            <div className="mt-8" data-testid="lc-erasure-section">
              <h2 className="font-display text-lg font-semibold mb-1">GDPR / DPDP erasure</h2>
              <p className="text-sm text-slate-500 mb-3">Departed people can be anonymised in place. Financial & audit records are retained under legal-basis exemption; a tamper-evident deletion certificate is issued.</p>
              <div className="bg-white border border-slate-200 rounded-md p-4 mb-3" data-testid="retention-settings">
                <div className="flex items-end gap-2 flex-wrap">
                  <div>
                    <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Retention schedule (auto-flags due records weekly)</p>
                    <p className="text-xs text-slate-500">Current: staff {retention?.[0]?.staff_days ?? 365}d · students {retention?.[0]?.student_days ?? 730}d after departure {retention?.[0]?.default && '(defaults - not saved yet)'}</p>
                  </div>
                  <Input data-testid="retention-staff-input" className="w-28" type="number" placeholder="Staff days" value={retForm.staff_days} onChange={(e) => setRetForm({ ...retForm, staff_days: e.target.value })} />
                  <Input data-testid="retention-student-input" className="w-32" type="number" placeholder="Student days" value={retForm.student_days} onChange={(e) => setRetForm({ ...retForm, student_days: e.target.value })} />
                  <Button data-testid="retention-save-button" variant="outline" disabled={retMut.isPending} onClick={() => retMut.mutate()}>Save schedule</Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input data-testid="retention-auto-checkbox" type="checkbox" checked={retAuto} onChange={(e) => setRetAuto(e.target.checked)} className="w-4 h-4 accent-slate-900" />
                    Auto-erase due records after an approval window of
                  </label>
                  <Input data-testid="retention-grace-input" className="w-20" type="number" value={retGrace} onChange={(e) => setRetGrace(e.target.value)} />
                  <span className="text-sm text-slate-700">days (weekly scan performs the erasure and issues certificates)</span>
                </div>
                {erasure?.due_count > 0 && <p className="text-xs text-rose-600 font-semibold mt-2" data-testid="retention-due-count">{erasure.due_count} record(s) past retention - due for erasure now.</p>}
              </div>
              <div className="space-y-2 mb-4" data-testid="lc-erasure-eligible">
                {(erasure?.staff || []).map((u) => (
                  <div key={u.id} className="bg-white border border-slate-200 rounded-md p-3 text-sm flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{u.name || u.email}</span>
                    <Badge tone="slate">staff · {u.role?.replace(/_/g, ' ')}</Badge>
                    <Badge tone="rose">departed</Badge>
                    {u.retention_due ? <Badge tone="rose">due for erasure</Badge> : u.due_in_days != null && <Badge tone="amber">due in {u.due_in_days}d</Badge>}
                    <button data-testid={`erase-staff-${u.id}`} onClick={() => doErase('staff', u.id, u.name || u.email)}
                      className="text-xs border border-rose-200 text-rose-600 rounded px-2 py-1 hover:bg-rose-50 ml-auto">Anonymise & purge</button>
                  </div>
                ))}
                {(erasure?.students || []).map((s) => (
                  <div key={s.id} className="bg-white border border-slate-200 rounded-md p-3 text-sm flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{s.name}</span>
                    <Badge tone="slate">student · grade {s.grade}{s.section}</Badge>
                    <Badge tone="rose">exited</Badge>
                    {s.retention_due ? <Badge tone="rose">due for erasure</Badge> : s.due_in_days != null && <Badge tone="amber">due in {s.due_in_days}d</Badge>}
                    <button data-testid={`erase-student-${s.id}`} onClick={() => doErase('student', s.id, s.name)}
                      className="text-xs border border-rose-200 text-rose-600 rounded px-2 py-1 hover:bg-rose-50 ml-auto">Anonymise & purge</button>
                  </div>
                ))}
                {(erasure?.staff || []).length === 0 && (erasure?.students || []).length === 0 && (
                  <p className="text-sm text-slate-400" data-testid="lc-erasure-empty">No departed records awaiting erasure.</p>
                )}
              </div>
              {(certs || []).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="lc-certificates">
                  <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Deletion certificates</p>
                  <div className="space-y-1.5">
                    {certs.map((c) => (
                      <div key={c.id} className="text-xs border-b border-slate-100 pb-1.5">
                        <span className="font-mono-label font-bold">{c.certificate_hash.slice(0, 20)}...</span>
                        <span className="text-slate-500"> · {c.subject_was} · {c.purged_at?.slice(0, 16).replace('T', ' ')} · by {c.purged_by}</span>
                        <p className="text-slate-400">{c.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
